import { FlexColumn } from "@/components/Flex";
import { spacing } from "@/utils/spacing";
import { TranscriptEnum } from "@knowt/syncing/graphql/schema";
import { Transcript, Utterance } from "@knowt/syncing/types/common";
import { type ReactNode, useMemo } from "react";
import { MediaPlayerContextProvider } from "../hooks/useMediaPlayerContext";
import { MediaTranscriptions } from "./MediaTranscriptions";

type Props = {
    transcript: Transcript;
    utterances: Utterance;
    children: ReactNode;
    containerStyle?: React.CSSProperties;
    transcriptType: TranscriptEnum;
};

export const MediaViewerWithTranscripts = ({
    transcript,
    utterances,
    children,
    containerStyle,
    transcriptType,
}: Props) => {
    const transcriptWithUtterances = useMemo(() => {
        if (!transcript?.length || !utterances?.length) return undefined;

        if (!utterances?.length)
            return transcript?.map(sentence => ({
                ...sentence,
                utterances: [
                    {
                        word: sentence.content,
                        start: sentence.start,
                        end: sentence.end,
                        confidence: 1,
                        punctuated_word: sentence.content,
                    },
                ] as Utterance,
            }));

        return transcript.map(chapter => ({
            ...chapter,
            utterances: utterances.filter(
                utterance => utterance.start >= chapter.start && utterance.end <= chapter.end
            ),
        }));
    }, [transcript, utterances]);

    return (
        <MediaPlayerContextProvider>
            <FlexColumn style={{ gap: spacing.MD, overflow: "auto", maxHeight: "100%" }}>
                {children}
                {transcriptWithUtterances && (
                    <MediaTranscriptions
                        chapters={transcriptWithUtterances}
                        utterances={
                            utterances ||
                            // biome-ignore lint/performance/noAccumulatingSpread: I don't know how to fix this lint
                            transcriptWithUtterances.reduce((acc, chapter) => [...acc, ...chapter.utterances], [])
                        }
                        containerStyle={containerStyle}
                        transcriptType={transcriptType}
                    />
                )}
            </FlexColumn>
        </MediaPlayerContextProvider>
    );
};
